// Calculated fields for the Visual designer.
//
// A calculation is a formula over the data source's own fields, written the
// way a business user reads them — `[Gross margin] / [Net sales] * 100` — and
// run IN THE DATABASE as part of the visual's query. Nothing new runs it:
// it compiles to the column `expression` every engine (platform, downloaded
// runtime, J2EE) already emits verbatim, with a `FUNCTION` the expression
// itself contains, so the engines leave it as written and keep it out of the
// GROUP BY.
//
// Two ways to work it out (`when`):
//   total → each field is added up first (its own aggregation), THEN the
//           formula runs on the totals — ratios and percentages:
//           SUM(a) * 1.0 / NULLIF(SUM(b), 0) * 100
//   row   → the formula runs on every record, THEN the results are added up —
//           amounts such as Quantity × Rate: SUM(a * b)
//
// Where it is saved:
//   SQLMODEL.CALCULATIONS = the definitions (label, formula, when) — the
//     Calculations folder of the field tree, placed or not;
//   a PLACED calculation is an ordinary column carrying `expression`,
//     `FUNCTION`, and `CALC: {key, formula, when, refs}` — `refs` are the
//     real columns it reads, which the engines use to bring their tables into
//     the FROM and to hide the calculation from anyone a column-security
//     policy hides or masks one of them from.
//
// Every problem carries `at` — where it is in the formula text — so the
// editor marks the exact spot instead of failing silently.

import type { BankColumn } from './dataSource'
import type { VisualColumn, VisualModel } from './types'

export type CalcWhen = 'total' | 'row'

export interface VisualCalculation {
  key: string
  label: string
  formula: string
  when: CalcWhen
}

/** A column reference a calculation reads (schema mode). */
export interface CalcRef {
  DBTABLE: string
  DBNAME: string
}

/** What a placed calculation column carries under `CALC`. */
export interface CalcColumnInfo {
  key: string
  formula: string
  when: CalcWhen
  refs: CalcRef[]
}

/** [start, end) in the formula text. start === end marks a gap (something missing there). */
export type FxSpan = [number, number]

export interface FxProblem {
  message: string
  at: FxSpan | null
}

export const FX_FUNCTIONS: Record<string, { min: number; max: number; help: string }> = {
  ROUND: { min: 1, max: 2, help: 'ROUND(x, places) — round to whole numbers or to places' },
  ABS: { min: 1, max: 1, help: 'ABS(x) — drop the minus sign' },
  MIN: { min: 2, max: 9, help: 'MIN(a, b) — the smaller one' },
  MAX: { min: 2, max: 9, help: 'MAX(a, b) — the larger one' },
}

/** Aggregations a field may use inside an After-totals calculation. They are
 *  spelled the same on every supported database; MEDIAN / STDDEV / … are not,
 *  so a field aggregated that way is refused with a reason. */
const PORTABLE_AGGS: Record<string, (ref: string) => string> = {
  SUM: (r) => `SUM(${r})`,
  COUNT: (r) => `COUNT(${r})`,
  COUNT_DISTINCT: (r) => `COUNT(DISTINCT ${r})`,
  AVG: (r) => `AVG(${r})`,
  MIN: (r) => `MIN(${r})`,
  MAX: (r) => `MAX(${r})`,
}

const AGG_WORDS: Record<string, string> = {
  SUM: 'added up', COUNT: 'counted', COUNT_DISTINCT: 'counted (distinct)', AVG: 'averaged',
  MIN: 'the smallest', MAX: 'the largest',
}

export const CALC_FOLDER = 'Calculations'
export const CALC_KEY_PREFIX = 'ƒ:'

// ── Parsing ─────────────────────────────────────────────────────────

type Tok =
  | { t: 'ref'; v: string; s: number; e: number }
  | { t: 'num'; v: string; s: number; e: number }
  | { t: 'id'; v: string; raw: string; s: number; e: number }
  | { t: 'op'; v: string; s: number; e: number }

export type FxNode =
  | { k: 'num'; v: string }
  | { k: 'ref'; name: string; s: number; e: number }
  | { k: 'neg'; a: FxNode }
  | { k: 'grp'; a: FxNode }
  | { k: 'bin'; op: '+' | '-' | '*' | '/'; a: FxNode; b: FxNode }
  | { k: 'fn'; name: string; args: FxNode[] }

export type FxParse = { ast: FxNode } | { empty: true } | { problem: FxProblem }

class FxError extends Error {
  at: FxSpan
  constructor(message: string, at: FxSpan) { super(message); this.at = at }
}

const showOp = (v: string) => (v === '*' ? '×' : v === '/' ? '÷' : v === '-' ? '−' : v)

/** Parse a formula. Field names go in [square brackets]; numbers, + − × ÷,
 *  brackets and ROUND / ABS / MIN / MAX are the whole language. */
export function parseFormula(src: string): FxParse {
  const s = String(src ?? '')
  const toks: Tok[] = []
  const err = (message: string, a: number, b?: number): FxParse =>
    ({ problem: { message, at: [a, b ?? a + 1] } })
  let i = 0
  while (i < s.length) {
    const ch = s[i]
    if (/\s/.test(ch)) { i++; continue }
    if (ch === '[') {
      const j = s.indexOf(']', i)
      if (j < 0) return err('A field name is missing its closing ]', i, s.length)
      toks.push({ t: 'ref', v: s.slice(i + 1, j).trim(), s: i, e: j + 1 })
      i = j + 1
      continue
    }
    if (ch === ']') return err('There is a ] with no [ before it', i)
    if (/[0-9.]/.test(ch)) {
      const m = /^(?:[0-9]+(?:\.[0-9]+)?|\.[0-9]+)/.exec(s.slice(i))
      if (!m) return err(`“${ch}” is not a number`, i)
      toks.push({ t: 'num', v: m[0].startsWith('.') ? `0${m[0]}` : m[0], s: i, e: i + m[0].length })
      i += m[0].length
      continue
    }
    if (/[A-Za-z_]/.test(ch)) {
      const m = /^[A-Za-z_]\w*/.exec(s.slice(i))!
      toks.push({ t: 'id', v: m[0].toUpperCase(), raw: m[0], s: i, e: i + m[0].length })
      i += m[0].length
      continue
    }
    if ('+-*/(),×÷−'.includes(ch)) {
      const v = ch === '×' ? '*' : ch === '÷' ? '/' : ch === '−' ? '-' : ch
      toks.push({ t: 'op', v, s: i, e: i + 1 })
      i++
      continue
    }
    return err(`“${ch}” can’t be used in a formula`, i)
  }
  if (!toks.length) return { empty: true }

  const end = s.replace(/\s+$/, '').length
  let p = 0
  const peek = () => toks[p] as Tok | undefined
  const isOp = (v: string) => { const t = peek(); return !!t && t.t === 'op' && t.v === v }
  const fail = (message: string, a?: Tok, b?: Tok): never => {
    throw new FxError(message, a ? [a.s, (b || a).e] : [end, end])
  }

  const expr = (): FxNode => {
    let a = term()
    while (isOp('+') || isOp('-')) {
      const o = toks[p++]
      if (!peek()) fail(`Something is missing after “${showOp(o.v)}”`, o)
      a = { k: 'bin', op: o.v as '+' | '-', a, b: term() }
    }
    return a
  }
  const term = (): FxNode => {
    let a = factor()
    while (isOp('*') || isOp('/')) {
      const o = toks[p++]
      if (!peek()) fail(`Something is missing after “${showOp(o.v)}”`, o)
      a = { k: 'bin', op: o.v as '*' | '/', a, b: factor() }
    }
    return a
  }
  const factor = (): FxNode => {
    const t = peek()
    if (!t) return fail('The formula ends too early')
    if (t.t === 'op' && t.v === '-') { p++; return { k: 'neg', a: factor() } }
    if (t.t === 'num') { p++; return { k: 'num', v: t.v } }
    if (t.t === 'ref') {
      p++
      if (!t.v) fail('[ ] needs a field name inside', t)
      return { k: 'ref', name: t.v, s: t.s, e: t.e }
    }
    if (t.t === 'op' && t.v === '(') {
      p++
      const e = expr()
      if (!isOp(')')) fail('This bracket ( is never closed', t)
      p++
      return { k: 'grp', a: e }
    }
    if (t.t === 'id') {
      const spec = FX_FUNCTIONS[t.v]
      p++
      if (!spec) {
        fail(isOp('(')
          ? `There is no function ${t.raw} — use ROUND, ABS, MIN or MAX`
          : `Put field names in square brackets: [${t.raw}]`, t)
      }
      if (!isOp('(')) fail(`${t.v} needs brackets: ${t.v}( … )`, t)
      const open = toks[p++]
      const args: FxNode[] = []
      if (!isOp(')')) {
        args.push(expr())
        while (isOp(',')) { p++; args.push(expr()) }
      }
      if (!isOp(')')) fail('This bracket ( is never closed', open)
      const close = toks[p++]
      if (args.length < spec.min || args.length > spec.max) {
        const n = args.length < spec.min ? spec.min : spec.max
        fail(`${t.v} ${args.length < spec.min ? 'needs at least' : 'takes at most'} ${n} value${n === 1 ? '' : 's'} — ${spec.help}`, t, close)
      }
      const plainNumber = (x: FxNode) => x.k === 'num' || (x.k === 'neg' && x.a.k === 'num')
      if (t.v === 'ROUND' && args[1] && !plainNumber(args[1])) {
        fail('ROUND’s places must be a plain number, e.g. ROUND([Margin], 1)', t, close)
      }
      return { k: 'fn', name: t.v, args }
    }
    if (t.v === ')') return fail('There is a ) with no ( before it', t)
    if (t.v === ',') return fail('A comma only goes between the values of a function, e.g. MAX(a, b)', t)
    return fail(`Something is missing before “${showOp(t.v)}”`, t)
  }

  try {
    const ast = expr()
    if (p < toks.length) {
      const t = toks[p]
      if (t.t === 'op' && t.v === ')') fail('There is a ) with no ( before it', t)
      const shown = t.t === 'ref' ? `[${t.v}]` : t.t === 'id' ? t.raw : showOp(t.v)
      fail(`Something is missing before “${shown}” — an operator such as + or ×`, t)
    }
    return { ast }
  } catch (e) {
    if (e instanceof FxError) return { problem: { message: e.message, at: e.at } }
    return { problem: { message: (e as Error)?.message || 'The formula could not be read', at: null } }
  }
}

export function formulaRefs(n: FxNode, out: Array<Extract<FxNode, { k: 'ref' }>> = []) {
  if (n.k === 'ref') out.push(n)
  else if (n.k === 'neg' || n.k === 'grp') formulaRefs(n.a, out)
  else if (n.k === 'bin') { formulaRefs(n.a, out); formulaRefs(n.b, out) }
  else if (n.k === 'fn') n.args.forEach((x) => formulaRefs(x, out))
  return out
}

/** Split the formula around a problem, for the marked copy under the error. */
export function markProblem(formula: string, at: FxSpan | null) {
  if (!at) return null
  const s = String(formula ?? '')
  const a = Math.max(0, Math.min(at[0], s.length))
  const b = Math.max(a, Math.min(at[1], s.length))
  return { before: s.slice(0, a), bad: s.slice(a, b), gap: b <= a, after: s.slice(b) }
}

/** Edit distance, for "did you mean". */
function lev(a: string, b: string): number {
  const d: number[][] = []
  for (let i = 0; i <= a.length; i++) {
    d[i] = [i]
    for (let j = 1; j <= b.length; j++) {
      d[i][j] = i === 0 ? j
        : Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1))
    }
  }
  return d[a.length][b.length]
}

// ── Compiling ───────────────────────────────────────────────────────

export interface CompiledCalc {
  /** The column `expression` the engines emit. */
  expression: string
  /** The column `FUNCTION` — an aggregate the expression itself contains, so
   *  the engines keep the expression as written (never re-wrap it). */
  fn: string
  refs: CalcRef[]
  /** The whole SQL the database runs for it — what "How it runs" shows. */
  runs: string
}

export type CalcResult = { ok: true; compiled: CompiledCalc } | { ok: false; problem: FxProblem }

const bareTable = (t: string) => (t || '').trim().split('.').pop() || ''

function aggOf(b: BankColumn): string {
  const fn = (b.defaultFunction || '').trim().toUpperCase()
  if (fn) return fn
  return b.type === 'number' ? 'SUM' : ''
}

/** Compile one calculation against the data source's fields and the other
 *  calculations (which it may use by name). */
export function compileCalculation(
  calc: Pick<VisualCalculation, 'key' | 'label' | 'formula' | 'when'>,
  bank: BankColumn[],
  calcs: VisualCalculation[],
): CalcResult {
  const fields = new Map<string, BankColumn>()
  for (const b of bank) if (!b.calcKey) fields.set((b.label || b.name).trim().toLowerCase(), b)
  // Every calculation by name — this one as it is being edited, not as saved.
  const byName = new Map<string, Pick<VisualCalculation, 'key' | 'label' | 'formula' | 'when'>>()
  for (const c of calcs) if (c.key !== calc.key) byName.set(c.label.trim().toLowerCase(), c)
  const selfKey = calc.key || '__new'
  if ((calc.label || '').trim()) byName.set(calc.label.trim().toLowerCase(), { ...calc, key: selfKey })

  const refs: CalcRef[] = []
  const addRef = (b: BankColumn) => {
    const r = { DBTABLE: b.DBTABLE || '', DBNAME: b.DBNAME || '' }
    if (!refs.some((x) => x.DBTABLE === r.DBTABLE && x.DBNAME === r.DBNAME)) refs.push(r)
  }

  // `chain` = calculations being compiled, outermost first (cycle guard).
  const sqlOf = (n: FxNode, inRow: boolean, chain: string[]): string => {
    switch (n.k) {
      case 'num': return n.v
      case 'grp': return `(${sqlOf(n.a, inRow, chain)})`
      case 'neg': return `(-${sqlOf(n.a, inRow, chain)})`
      case 'bin': {
        const a = sqlOf(n.a, inRow, chain)
        const b = sqlOf(n.b, inRow, chain)
        // × 1.0 keeps integer counts from dividing as whole numbers, and
        // NULLIF turns a division by zero into "no value" instead of an error.
        return n.op === '/' ? `${a} * 1.0 / NULLIF(${b}, 0)` : `${a} ${n.op} ${b}`
      }
      case 'fn': {
        const args = n.args.map((x) => sqlOf(x, inRow, chain))
        if (n.name === 'ROUND') return `ROUND(${args[0]}, ${args[1] ?? '0'})`
        if (n.name === 'ABS') return `ABS(${args[0]})`
        // MIN / MAX of several values: a CASE every database reads the same
        // (LEAST / GREATEST are missing on some).
        const cmp = n.name === 'MIN' ? '<=' : '>='
        return args.reduce((acc, x) => `CASE WHEN ${acc} ${cmp} ${x} THEN ${acc} ELSE ${x} END`)
      }
      case 'ref': {
        const at: FxSpan = [n.s, n.e]
        const lc = n.name.toLowerCase()
        const other = byName.get(lc)
        if (other) {
          if (other.key === selfKey && chain.length <= 1) throw new FxError('A calculation can’t use itself', at)
          if (chain.includes(other.key)) {
            throw new FxError(`[${other.label}] uses this calculation, so using it here goes round in a circle`, at)
          }
          if (inRow && other.when === 'total') {
            throw new FxError(`[${other.label}] is worked out after totals, so it can’t be used row by row`, at)
          }
          const parsed = parseFormula(other.formula)
          if (!('ast' in parsed)) throw new FxError(`[${other.label}] has a problem of its own — open it and fix that first`, at)
          if (other.when === 'row') {
            const rowSql = sqlOf(parsed.ast, true, [...chain, other.key])
            return inRow ? `(${rowSql})` : `SUM(${rowSql})`
          }
          return `(${sqlOf(parsed.ast, false, [...chain, other.key])})`
        }
        const b = fields.get(lc)
        if (!b) {
          const names = [...[...fields.values()].map((x) => x.label || x.name),
            ...[...byName.values()].filter((x) => x.key !== selfKey).map((x) => x.label)]
          const best = names
            .map((x) => [x, lev(x.toLowerCase(), lc)] as const)
            .sort((x, y) => x[1] - y[1])[0]
          const hint = best && best[1] <= Math.max(3, lc.length / 2) ? ` — did you mean [${best[0]}]?` : ''
          throw new FxError(`No field called [${n.name}]${hint}`, at)
        }
        if (!b.DBNAME) throw new FxError(`[${b.label}] is not a column of the data source, so it can’t be used here`, at)
        const ref = `${bareTable(b.DBTABLE || '')}${b.DBTABLE ? '.' : ''}${b.DBNAME}`
        if (inRow) {
          if (b.type !== 'number') throw new FxError(`[${b.label}] is not a number, so it can’t be used row by row`, at)
          addRef(b)
          return ref
        }
        const fn = aggOf(b)
        if (!fn) {
          throw new FxError(`[${b.label}] is ${b.type === 'date' ? 'a date' : 'text'} — a calculation can only use numbers (or a count)`, at)
        }
        const agg = PORTABLE_AGGS[fn]
        if (!agg) {
          throw new FxError(`[${b.label}] is worked out with ${fn}, which can’t be used inside a calculation — `
            + 'only fields that are added up, counted, averaged, or take the smallest / largest value', at)
        }
        addRef(b)
        return agg(ref)
      }
    }
  }

  const parsed = parseFormula(calc.formula)
  if ('empty' in parsed) return { ok: false, problem: { message: 'Type a formula, or start from one above', at: null } }
  if ('problem' in parsed) return { ok: false, problem: parsed.problem }
  const inRow = calc.when === 'row'
  let body: string
  try {
    body = sqlOf(parsed.ast, inRow, [selfKey])
  } catch (e) {
    if (e instanceof FxError) return { ok: false, problem: { message: e.message, at: e.at } }
    return { ok: false, problem: { message: (e as Error)?.message || 'The formula could not be worked out', at: null } }
  }
  if (!refs.length) {
    return { ok: false, problem: { message: 'Use at least one field — a formula of plain numbers gives the same value everywhere', at: null } }
  }
  if (inRow) return { ok: true, compiled: { expression: body, fn: 'SUM', refs, runs: `SUM(${body})` } }
  const fn = (/\b(SUM|COUNT|AVG|MIN|MAX)\(/.exec(body) || [])[1] || 'SUM'
  return { ok: true, compiled: { expression: body, fn, refs, runs: body } }
}

// ── The model ───────────────────────────────────────────────────────

export function calculationsOf(m: VisualModel): VisualCalculation[] {
  const raw = (m.SQLMODEL as { CALCULATIONS?: unknown }).CALCULATIONS
  if (!Array.isArray(raw)) return []
  return raw
    .filter((c): c is Record<string, unknown> => !!c && typeof c === 'object')
    .map((c) => ({
      key: String(c.key || ''),
      label: String(c.label || ''),
      formula: String(c.formula || ''),
      when: (c.when === 'row' ? 'row' : 'total') as CalcWhen,
    }))
    .filter((c) => c.key && c.label)
}

/** The calculation a placed column is, if any. */
export function calcInfoOf(col: VisualColumn | undefined | null): CalcColumnInfo | null {
  const raw = col?.CALC
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  return r.key ? r as unknown as CalcColumnInfo : null
}

export function nextCalcKey(calcs: VisualCalculation[]): string {
  let n = 1
  const used = new Set(calcs.map((c) => c.key))
  while (used.has(`calc_${n}`)) n++
  return `calc_${n}`
}

/** The Calculations folder of the field tree: one bank entry per calculation,
 *  with the problem that stops it running (if any). */
export function calcBankColumns(calcs: VisualCalculation[], bank: BankColumn[]): BankColumn[] {
  return calcs.map((c) => {
    const res = compileCalculation(c, bank, calcs)
    return {
      key: `${CALC_KEY_PREFIX}${c.key}`,
      label: c.label,
      name: c.key.toUpperCase(),
      tableLabel: CALC_FOLDER,
      type: 'number' as const,
      COLTYPE: 'NUMBER' as const,
      calcKey: c.key,
      calcFormula: c.formula,
      calcProblem: res.ok ? '' : res.problem.message,
      calcDef: c,
      calcCompiled: res.ok ? res.compiled : undefined,
    }
  })
}

/** Write a calculation's compiled form onto a column (new or placed). */
export function applyCalcToColumn(col: VisualColumn, calc: VisualCalculation, compiled: CompiledCalc) {
  col.NAME = calc.key.toUpperCase()
  col.DBNAME = calc.key.toUpperCase()
  // The first table it reads: an engine that predates CALC.refs still puts
  // that table in the FROM.
  col.DBTABLE = compiled.refs[0]?.DBTABLE || ''
  col.tableName = col.DBTABLE
  col.content = calc.label
  col.name = calc.label
  col.type = 'number'
  col.COLTYPE = 'NUMBER'
  col.JAVATYPE = 'java.math.BigDecimal'
  col.expression = compiled.expression
  col.FUNCTION = compiled.fn
  col.DEFAULTFUNCTION = compiled.fn
  col.EXPRESSIONTYPE = 'G'
  col.CALC = { key: calc.key, formula: calc.formula, when: calc.when, refs: compiled.refs } satisfies CalcColumnInfo
}

/** After the calculations changed: re-compile every placed calculation column,
 *  drop the ones whose calculation was deleted. A calculation that no longer
 *  compiles keeps its last good expression (the field tree flags it). */
export function refreshCalcColumns(m: VisualModel, bank: BankColumn[]) {
  const calcs = calculationsOf(m)
  const byKey = new Map(calcs.map((c) => [c.key, c]))
  for (const g of m.LAYOUT_DATA.visualLayout.ColumnGroups || []) {
    g.COLUMNS = (g.COLUMNS || []).filter((col) => {
      const info = calcInfoOf(col)
      if (!info) return true
      const calc = byKey.get(info.key)
      if (!calc) return false
      const res = compileCalculation(calc, bank, calcs)
      if (res.ok) applyCalcToColumn(col, calc, res.compiled)
      else { col.content = calc.label; col.name = calc.label }
      return true
    })
  }
}

/** Rename references to a calculation inside the other calculations' formulas. */
export function renameCalcRefs(calcs: VisualCalculation[], skipKey: string, from: string, to: string) {
  const re = new RegExp(`\\[\\s*${from.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\]`, 'gi')
  for (const c of calcs) if (c.key !== skipKey) c.formula = c.formula.replace(re, `[${to}]`)
}

/** Calculations that use the given one by name. */
export function calcsUsing(calcs: VisualCalculation[], target: VisualCalculation): VisualCalculation[] {
  const lc = target.label.trim().toLowerCase()
  return calcs.filter((c) => {
    if (c.key === target.key) return false
    const parsed = parseFormula(c.formula)
    return 'ast' in parsed && formulaRefs(parsed.ast).some((r) => r.name.trim().toLowerCase() === lc)
  })
}

/** Ready-made formulas, offered only when the data source has the fields they need. */
export const CALC_STARTERS: Array<{ label: string; formula: string; when: CalcWhen; about: string }> = [
  { label: 'Margin %', formula: '[Gross margin] / [Net sales] * 100', when: 'total', about: 'margin as a share of sales' },
  { label: 'Net after discount', formula: '[Net sales] - [Discount]', when: 'row', about: 'sales less discount' },
  { label: 'Achievement %', formula: '[Net sales] / [Sales target] * 100', when: 'total', about: 'actual against target' },
  { label: 'Average order value', formula: '[Net sales] / [Orders]', when: 'total', about: 'sales per order' },
  { label: 'Discount %', formula: '[Discount] / [Net sales] * 100', when: 'total', about: 'discount as a share of sales' },
]
